/**
 * metric_dictionary.js — Central definitions for every metric shown in
 * Customer Intelligence, in plain Arabic that non-technical users can
 * understand at a glance.
 *
 * Design principles (owner directive 2026-07-23):
 *   1. ONE dictionary drives explanations across the whole app — no more
 *      per-screen tooltips with divergent wording.
 *   2. Each entry states WHAT the metric means and HOW it is computed,
 *      in plain Arabic — no code, no thresholds without context.
 *   3. Shape is extensible: `segments`, `kpi`, `ratio`, `benchmark`, …
 *      This first slice only fills `segments`. Future work populates the
 *      others without touching the Explainer component.
 *
 * Rule values MUST stay in sync with `_SEED_DEFINITIONS` in
 * `/app/backend/services/segments.py` — this dictionary is the "human"
 * translation layer of those rules.
 */

const CANONICAL_SOURCE_LINE =
  "المصدر: فواتير رتم الحيّة (جدول pos_sales) خلال آخر 90 يومًا.";

export const METRIC_DICTIONARY = {
  segments: {
    gold_vip: {
      title: "\u{1F451} ذهبي VIP",
      color: "#FFB800",
      one_line: "قمة نشاط عملائك — الفئة الأثمن في المقهى.",
      rule_arabic:
        "عميل زار على الأقل 18 مرة، وأنفق ما مجموعه 150 ريالًا أو أكثر، وآخر زيارة له خلال 45 يومًا.",
      how_to_use:
        "لا تُرسل لهم خصومات — أرسل امتنانًا. اتصل شخصيًا حين يغيبون.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 20 (يتم فحصها قبل rising_star و loyal).",
    },
    rising_star: {
      title: "\u{1F31F} نجم صاعد",
      color: "#FF9500",
      one_line: "مرشح VIP القادم — قليل الزيارات لكن متوسط فاتورته عالٍ.",
      rule_arabic:
        "عميل زار من مرتين إلى 17 مرة خلال آخر 45 يومًا، ومتوسط قيمة فاتورته 34 ريالًا أو أكثر.",
      how_to_use:
        "استضِفه لتسريع الترقّي إلى VIP — دعوة لتجربة صنف جديد أو ركن هادئ.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 30.",
    },
    loyal: {
      title: "\u{2728} وفي منتظم",
      color: "#5856D6",
      one_line: "قاعدة المقهى — يزور بانتظام دون تميّز سعري.",
      rule_arabic:
        "عميل زار 6 مرات أو أكثر خلال آخر 45 يومًا، ولم يستوفِ شروط VIP.",
      how_to_use:
        "شكر بسيط + نقاط ولاء. لا يحتاج خصومات؛ يحتاج أن يشعر أنك تعرفه.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 40.",
    },
    at_risk: {
      title: "\u{1F6A8} معرّض للفقد",
      color: "#FF3B30",
      one_line: "كان عميلًا قيّمًا ثم بدأ يبتعد — يجب التدخّل الآن.",
      rule_arabic:
        "عميل آخر زيارة له بين 46 و90 يومًا مضت، وكان مؤثّرًا قبل ذلك (زار 6 مرات أو أكثر، أو أنفق 150 ريالًا أو أكثر).",
      how_to_use:
        "أولوية استرجاع فوري: رسالة شخصية أو مكالمة، لا حملة عامة.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 50.",
    },
    promising: {
      title: "\u{1F331} واعد",
      color: "#34C759",
      one_line: "زار مرة أو اثنتين مؤخرًا بفاتورة جيدة — قابل للترقّي.",
      rule_arabic:
        "عميل زار مرة أو مرتين خلال آخر 14 يومًا، ومتوسط قيمة فاتورته 24 ريالًا أو أكثر.",
      how_to_use:
        "حفّزه لثالثة: عرض جرّب صنفًا جديدًا أو اجعل زيارته الثالثة بمكافأة.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 60.",
    },
    new: {
      title: "\u{1F195} ضيف جديد",
      color: "#0A84FF",
      one_line: "زيارته الأولى في المقهى — الترحيب يقرر عودته.",
      rule_arabic:
        "عميل عنده زيارة واحدة فقط، وحصلت خلال آخر 30 يومًا.",
      how_to_use:
        "رسالة واتساب ترحيبية فورية، شكر باسمه، ودعوة لجولة تعرّف على المكان.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 70.",
    },
    regular: {
      title: "\u{1F464} منتظم",
      color: "#64D2FF",
      one_line: "متردد نشط بدون تميّز في التكرار أو السعر — قاعدة الحملات العامة.",
      rule_arabic:
        "عميل زار من مرتين إلى 5 مرات خلال آخر 45 يومًا.",
      how_to_use:
        "الحملات الشاملة (عروض موسمية / مناسبات) توصله بأقل تكلفة.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 80.",
    },
    dormant: {
      title: "\u{1F634} خامل قابل للاسترجاع",
      color: "#AF52DE",
      one_line: "زار عدة مرات ثم اختفى شهرًا فأكثر بإنفاق متواضع.",
      rule_arabic:
        "عميل آخر زيارة له بين 46 و90 يومًا مضت، زار من مرتين إلى 5 مرات، وإجمالي إنفاقه أقل من 150 ريالًا.",
      how_to_use:
        "حملة استرجاع خفيفة: تذكير برسالة قصيرة أو عرض بسيط.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 90.",
    },
    drifter: {
      title: "\u{1F343} عابر",
      color: "#BF5AF2",
      one_line: "جرّبك مرة أو اثنتين ولم يعُد — فرصة للحملة الشاملة.",
      rule_arabic:
        "عميل عنده زيارة أو اثنتان فقط، وآخر زيارة له بين 31 و90 يومًا مضت.",
      how_to_use:
        "حملة أوسع نطاقًا مع تذكير برسالة موسمية أو عرض تجربة صنف جديد.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 100.",
    },
    lost: {
      title: "\u{1F494} مفقود",
      color: "#8E8E93",
      one_line: "غاب أكثر من 90 يومًا — كلفة الاسترجاع عالية.",
      rule_arabic: "عميل آخر زيارة له قبل أكثر من 90 يومًا.",
      how_to_use:
        "لا استثمار فردي؛ فقط ضمن حملة شاملة لو أطلقناها.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "الأولوية 10 (الأعلى — تُفحص أولًا).",
    },
    unknown: {
      title: "\u{2753} غير معروف",
      color: "#48484A",
      one_line: "لا توجد بيانات كافية لتصنيف هذا العميل.",
      rule_arabic:
        "العميل بلا فواتير في الفترة المرصودة، أو بلا رقم جوال يمكن التعرّف به عليه.",
      how_to_use: "لا نستطيع مخاطبته — الأولوية توثيق بياناته أولًا.",
      source: CANONICAL_SOURCE_LINE,
      priority_note: "احتياطي فقط.",
    },
  },

  // Reserved for future extension — populate incrementally per owner
  // direction. Keep the same {title, one_line, rule_arabic, source} shape.
  kpi: {
    net_sales: {
      title: "\u{1F4B5} صافي المبيعات",
      color: "#0F766E",
      one_line: "إجمالي الفواتير المدفوعة بعد خصم المرتجعات.",
      rule_arabic:
        "مجموع قيم الفواتير المكتملة (سعرًا صافيًا بعد الخصم) خلال الفترة، مطروحًا منه أي مرتجعات مسجّلة في نفس الفترة.",
      how_to_use:
        "المؤشر الأساسي للأداء المالي اليومي/الأسبوعي/الشهري — إن ارتفع يعني نجاح تسويقي أو موسمي، وإن انخفض ابحث عن السبب فورًا.",
      source: "من فواتير رتم الحيّة، مع استثناء `refunded=True` وضمّ صفوف المرتجعات كسالب.",
      priority_note: null,
    },
    total_invoices: {
      title: "\u{1F9FE} عدد الفواتير",
      color: "#0891B2",
      one_line: "كم فاتورة نُفّذت خلال الفترة.",
      rule_arabic:
        "عدّ الفواتير المكتملة في الفترة (بلا شرط رقم عميل). كل فاتورة تُحسب مرة واحدة حتى لو تضمّنت عدة أصناف.",
      how_to_use:
        "قِس ذَروة الازدحام: 50 فاتورة في ساعة تعني اكتظاظ الكاشير — قد تحتاج موظفًا إضافيًا.",
      source: "من `pos_sales` مع فلترة `refunded != True`.",
      priority_note: null,
    },
    atv: {
      title: "\u{1F4B0} متوسط قيمة الفاتورة",
      color: "#D97706",
      one_line: "معدّل صرف العميل الواحد في الزيارة الواحدة.",
      rule_arabic:
        "صافي المبيعات ÷ عدد الفواتير. يعكس مدى إقناع العميل بإضافة أصناف (Upsell / Cross-sell) أو اختيار خيارات أعلى قيمة.",
      how_to_use:
        "لو ATV منخفض رغم كثرة الزيارات: درّب الموظفين على الاقتراح الإضافي أو أعِد النظر في تصميم القائمة (Menu Engineering).",
      source: "محسوب من `pos_sales`: sum(total_price) / count(*).",
      priority_note: null,
    },
    unique_customers: {
      title: "\u{1F464} عدد العملاء الفريدين",
      color: "#7C3AED",
      one_line: "كم شخص مختلف زار المقهى (بغضّ النظر عن عدد زياراته).",
      rule_arabic:
        "عدد أرقام الجوال المختلفة التي ظهرت في فواتير الفترة. الفواتير بلا رقم عميل (walk-in) لا تُحسب هنا.",
      how_to_use:
        "قياس اتساع القاعدة: هل تكتسب عملاء جددًا أم تعتمد على نفس القاعدة؟ ارفعه بالتسويق الخارجي (لوحات، سوشيال، شراكات).",
      source: "distinct(customer_phone) في `pos_sales` للفترة، مع استثناء القيم الفارغة.",
      priority_note: null,
    },
  },
  ratio: {
    // Phase 12 mandate — three distinct "conversion-like" metrics.
    // Each carries its scope INSIDE the name (owner directive 2026-07-25)
    // so the word "تحويل" never appears alone anywhere in the app.
    paid_purchase_rate: {
      title: "\u{1F4B3} نسبة الشراء المدفوع",
      color: "#0F766E",
      one_line: "من زار المقهى، كم منهم دفع فعلًا مقابل الطلب (لا كوب مجاني).",
      rule_arabic:
        "(عدد العملاء الفريدين الذين لديهم على الأقل فاتورة واحدة قيمتها > 0 ÷ إجمالي العملاء الفريدين خلال الفترة) × 100. تشمل جميع الفواتير، لا الحملات فقط.",
      how_to_use:
        "الرقم يعكس صحّة تحوّل الزائر إلى مشترٍ حقيقي. لو انخفض بعد حملة مجانية، فهذا مؤشر على أن كثيرًا من المسجّلين لم يتحوّلوا لعملاء دائمين.",
      source:
        "distinct(customer_phone WHERE total_price > 0) ÷ distinct(customer_phone) في `pos_sales` للفترة.",
      priority_note: null,
    },
    launch_registrant_conversion: {
      title: "\u{1F31F} نسبة تحويل مسجّلي حملة الافتتاح",
      color: "#7C3AED",
      one_line:
        "من سجّل في رابط حملة «59 كوبًا» ثم استخدم بعضها، كم منهم صار عميلًا يدفع بعد انتهاء العرض.",
      rule_arabic:
        "(عدد مسجّلي الحملة الذين اشتروا فاتورة مدفوعة واحدة على الأقل بعد انتهاء الحملة ÷ إجمالي مسجّلي الحملة الذين استخدموا كوبًا واحدًا على الأقل) × 100.",
      how_to_use:
        "هذا هو المؤشر الحقيقي لجدوى حملة الاكتساب. أرقام أقل من 15٪ تعني أن الحملة جذبت باحثين عن العرض لا عملاء محتملين.",
      source:
        "`datasets.type=cust` (المسجّلون) ⨯ `pos_sales` (الشراء بعد end_date) — من ملف `routes/launch_cohort.py`.",
      priority_note: null,
    },
    template_recipient_visit_rate: {
      title: "\u{1F4E9} نسبة زيارة المستلمين",
      color: "#D97706",
      one_line:
        "من قوالب واتساب الفعلية: كم من المستلمين زار المقهى خلال 7 أيام من الإرسال.",
      rule_arabic:
        "(عدد المستلمين الذين ظهرت لهم فاتورة في pos_sales خلال 7 أيام بعد الإرسال ÷ إجمالي المستلمين للحملة) × 100.",
      how_to_use:
        "أفضل مقياس لفاعلية القالب. أقل من 2٪ = قالب ضعيف. 5٪ فما فوق = قالب ممتاز.",
      source:
        "ربط سجل الإرسال (`wa_sends`) مع الزيارات في `pos_sales` بنافذة 7 أيام — من مسار قوالب واتساب.",
      priority_note: null,
    },
    return_rate: {
      title: "\u{1F501} معدّل عودة العميل",
      color: "#059669",
      one_line: "نسبة العملاء الذين زاروك مرتين أو أكثر خلال الفترة.",
      rule_arabic:
        "(عدد العملاء الذين لديهم ≥ زيارتين ÷ إجمالي العملاء الفريدين) × 100. مؤشر جودة تجربة العميل الفعلية.",
      how_to_use:
        "أقل من 30٪ = مشكلة تجربة (قهوة، خدمة، مكان). أعلى من 60٪ = ولاء ممتاز. راقب الاتجاه شهريًا.",
      source: "محسوب من `pos_sales` بتجميع الفواتير حسب رقم الجوال.",
      priority_note: null,
    },
    retention_30d: {
      title: "\u{1F4C8} نسبة الاحتفاظ (30 يوم)",
      color: "#0284C7",
      one_line: "من عملائك قبل شهر، كم عادوا هذا الشهر.",
      rule_arabic:
        "(عملاء زاروا خلال آخر 30 يوم من العملاء الذين زاروا في الشهر الذي قبله ÷ عملاء الشهر السابق) × 100.",
      how_to_use:
        "المؤشر الحقيقي للولاء بعيدًا عن الحملات القصيرة. تحسّنه يعني قاعدة عملاء صحيّة.",
      source: "مقارنة توزيع الجوالات بين نافذتَين متتاليتَين في `pos_sales`.",
      priority_note: null,
    },
    churn_rate: {
      title: "\u{1F4C9} معدّل الفقد (Churn)",
      color: "#DC2626",
      one_line: "نسبة العملاء الذين انقطعوا هذا الشهر بعد نشاط سابق.",
      rule_arabic:
        "(عملاء كانوا نشطين قبل 30-90 يومًا ولم يزوروا خلال آخر 30 يومًا ÷ عملاء الفترة السابقة) × 100.",
      how_to_use:
        "أي رقم فوق 20٪ يعني مشكلة كامنة. حلّل الشرائح: هل الفقد من VIP أم من العابرين؟ الجوابان يعنيان علاجًا مختلفًا.",
      source: "مقارنة نافذتَين متتاليتَين في `pos_sales` مع تحديد الغائبين.",
      priority_note: null,
    },
  },
  benchmark: {},

  // Phase 9 additions — cross-source glossary. Entries here explain how
  // to read data-source badges across the app.
  sources: {
    live_vs_historical: {
      title: "\u{1F504} الفرق بين البيانات الحيّة والتاريخية",
      color: "#0891B2",
      one_line: "متى تستخدم كل مصدر — وأيهما الأفضل لسؤالك.",
      rule_arabic:
        "«بيانات حيّة (RETM)»: تُقرأ لحظيًا من نظام الفواتير — تعكس ما يحدث في المقهى الآن. أفضل مصدر للقرارات السريعة (الأسبوع/الشهر الجاري). \n" +
        "«عرض تاريخي (Excel)»: يقرأ من آخر ملف Excel رفعته أنت — قد يحوي شهورًا أو سنوات سابقة. أفضل للتحليل الموسمي أو مقارنة سنة بسنة.",
      how_to_use:
        "افتح الشاشة تراك تلقائيًا على «الحيّة». إن كنت تدرس نمطًا موسميًا أو تريد الرجوع لبيانات قبل تفعيل رتم — اضغط «عرض تاريخي» ولاحظ تاريخ آخر رفع بجانب الشارة.",
      source:
        "المصدران مستقلان تمامًا: قد يختلفان في الأرقام لنفس الفترة إذا رفع Excel يحوي تصحيحات لم تصل رتم بعد، أو إذا كان Excel أقدم من التحديثات الحيّة.",
      priority_note: null,
    },
  },
};

/** Look up a metric entry by category + id. Falls back to null so callers
 * can render a soft empty state instead of crashing. */
export function getMetric(category, id) {
  const cat = METRIC_DICTIONARY[category];
  if (!cat) return null;
  return cat[id] || null;
}
